// ./src/components/features/Events/PreviewSiteButton.tsx
//
// ⓘ Guests-only pages (2026-10-03): also offered on a LIVE event's Website page when a page is kept for guests — the same
//   one-hour link then shows the crew every page (the site says "Crew preview"), which is otherwise locked to the host too.
//
// "Preview site" — opens the event's website AS GUESTS WILL SEE IT while the event is still a DRAFT (ROADMAP A2b #62
// P6(d4), owner 2026-09-30: "preview an event without publishing on the last wizard step before deciding to send an
// invitation … allowed only to crew"). A draft's site answers "not found" to everyone else, so this is the ONLY way in:
// it asks the API for a one-hour preview link (owner or crew) and opens it in a new tab.
//
// ⚠ The tab is opened INSIDE the click, before the request: a window opened after an await is a popup, and Safari and
//   every phone browser block it. It is then pointed at the link, with `opener` cut first (the site is another origin).
// ⚠ A refused or failed request closes that tab again and says why HERE — an empty tab left behind reads as "the preview
//   is blank".
// ⓘ Why a new component (the reuse gate): the finish screen and the event dashboard both need it, and nothing existing
//   opens a link that has to be MINTED first — every other site link is derived (`buildEventPublicUrl`).

import React, { useState } from 'react';
import { Anchor, Button, ButtonProps, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks';
import { EventPreviewLink, EventService } from '@/utils/api';
import { AppIcon } from '@/components/generic';
import { refusalReason } from '@/utils';

export interface PreviewSiteButtonProps {
  eventId: string;
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  /** Say a refusal in a toast rather than under the button — for a toolbar, where a line of text breaks the row. */
  errorAsToast?: boolean;
}

export const PreviewSiteButton: React.FC<PreviewSiteButtonProps> = ({ eventId, fullWidth, size = 'md', variant = 'default', errorAsToast = false }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The link, when the browser refused even the tab opened in the click — offered to tap rather than leaving the app.
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);

  const open = async () => {
    setError(null);
    setFallbackUrl(null);
    setOpening(true);

    const tab = window.open('about:blank', '_blank');
    let url = '';
    let reason: string | null = null;

    try {
      const res = await EventService.previewLink(eventId);

      url = res.didSucceed() ? ((res.getDataFirst() as EventPreviewLink | undefined)?.url ?? '') : '';
      reason = url ? null : refusalReason(res) || null;
    } catch {
      // A thrown request settles like a refused one — the tab is closed and the generic reason shown.
    }

    setOpening(false);

    if (!url) {
      const message = reason || t('events:preview.failed', { defaultValue: 'Could not open the preview. Please try again.' });

      tab?.close();

      if (errorAsToast) {
        toast.error(message);
      } else {
        setError(message);
      }

      return;
    }

    if (!tab) {
      // ⚠ The browser refused even the in-click tab. Never navigate THIS window there — the host would lose the screen
      //   they are on (the wizard's finish screen cannot be reopened). Offer the link to tap instead.
      setFallbackUrl(url);

      return;
    }

    tab.opener = null;
    tab.location.href = url;
  };

  return (
    <Stack gap={4} w={fullWidth ? '100%' : undefined} align={fullWidth ? 'stretch' : 'flex-start'}>
      <Button
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        loading={opening}
        leftSection={<AppIcon iconName="Eye" size={18} />}
        onClick={() => void open()}
      >
        {t('events:preview.button', { defaultValue: 'Preview site' })}
      </Button>
      {error ? (
        <Text size="md" c="red.7" ta="left">
          {error}
        </Text>
      ) : null}
      {fallbackUrl ? (
        <Anchor href={fallbackUrl} target="_blank" rel="noopener noreferrer" size="md" onClick={() => setFallbackUrl(null)}>
          {t('events:preview.openLink', { defaultValue: 'Your browser blocked the new tab — open the preview here' })}
        </Anchor>
      ) : null}
    </Stack>
  );
};
